import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  findAndReplace,
  guidRegex,
  sortAlphabetically,
} from '@appState/helpers';
import { deleteEntity, getAll, getAllWithParams, getOne, StateDictionary } from '@appState/helpers/store-base';
import { Community } from '@models/communities';
import { Recording, RecordingCreateRequest, RecordingParams, RecordingsCommunity, RecordingUpdateRequest } from '@models/index';
import { TopicOrder } from '@models/marketing/topics/topics.model';
import { patchState, signalStore, StateSignals, withComputed, withMethods, withState, WritableStateSource } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FileStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  getRecordingCommunityHandlingMethods,
  RecordingCommunityHandlingStoreType,
} from './recording-store-partials/recording-community-handling';
import { getRecordingConfigurationMethods, RecordingConfigurationStoreType } from './recording-store-partials/recording-configuration';
import { getRecordingsTopicsOrderMethods, RecordingTopicsOrderStoreType } from './recording-store-partials/recording-order';
import { RecordingsService } from './recordings.service';

export type RecordingsStore = StateSignals<RecordingsState> & WritableStateSource<RecordingsState>;

const sortRecordingsByCreatedAt = (a: Recording, b: Recording) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

type RecordingsState = {
  recordings: Recording[];
  isLoading: boolean;
  isRecordingCreateLoading: boolean;
  selectedRecording: Recording | null;
  isSelectedRecordingLoading: boolean;
  isFieldExpertsLoading: boolean;
  selectedRecordingCommunityId: string | null;

  recordingsByCommunity: Recording[];
  isRecordingsByCommunityLoading: boolean;

  topicsOrder: TopicOrder[];
  isTopicsOrderLoading: boolean;
  isSetTopicsOrderLoading: boolean;
};

const initialState: RecordingsState = {
  recordings: [],
  isLoading: false,
  isRecordingCreateLoading: false,
  selectedRecording: null,
  isSelectedRecordingLoading: false,
  isFieldExpertsLoading: false,
  selectedRecordingCommunityId: null,

  recordingsByCommunity: [],
  isRecordingsByCommunityLoading: false,

  topicsOrder: [],
  isTopicsOrderLoading: false,
  isSetTopicsOrderLoading: false,
};

const recordingsDictionary: StateDictionary<Recording> = {
  entityList: 'recordings',
  listLoading: 'isLoading',
  createLoading: 'isRecordingCreateLoading',
  selectedEntity: 'selectedRecording',
  selectedEntityLoading: 'isSelectedRecordingLoading',
  entityId: 'id',
  sortFn: sortRecordingsByCreatedAt,
};

const recordingsByCommunityDictionary: StateDictionary<Recording> = {
  ...recordingsDictionary,
  entityList: 'recordingsByCommunity',
  listLoading: 'isRecordingsByCommunityLoading',
};

export const RecordingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('recordings-store'),
  withComputed(store => ({
    selectedRecordingId: computed(() => store.selectedRecording()?.id || null),
    selectedRecordingCommunity: computed(() => {
      const recording = store.selectedRecording();
      const communityId = store.selectedRecordingCommunityId();
      if (recording && recording.communities?.length) {
        return recording.communities.find(c => c.id === communityId) || null;
      }
      return null;
    }),
  })),
  withMethods(
    (
      store,
      recordingsService = inject(RecordingsService),
      router = inject(Router),
      fileStorageService = inject(FileStorageService),
      notificationsService = inject(NotificationsService),
    ) => {
      createCustomStateSetOnUrlHandler<RecordingsState>('/marketing/recordings', {
        selectedRecording: null,
        selectedRecordingCommunityId: null,
      })(store);
      createCustomStateSetOnRegexHandler<RecordingsState>(new RegExp(`^/marketing/communities/${guidRegex}$`))(store, {
        recordingsByCommunity: [],
      });
      createCustomStateSetOnRegexHandler<RecordingsState>(new RegExp(`^/marketing/communities/${guidRegex}/recordings$`))(store, {
        selectedRecording: null,
      });
      createCustomStateSetOnRegexHandler<RecordingsState>(new RegExp(`^/marketing/recordings/${guidRegex}/communities$`))(store, {
        selectedRecordingCommunityId: null,
      });
      createCustomStateSetOnUrlHandler<RecordingsState>('/marketing/communities', { recordingsByCommunity: [] })(store);

      const recordingCommunitiesMethods = getRecordingCommunityHandlingMethods({
        store,
        recordingsService,
        router,
      });

      const recordingConfigurationMethods = getRecordingConfigurationMethods({
        store,
        recordingsService,
      });

      const recordingTopicsOrderStoreType = getRecordingsTopicsOrderMethods({
        store,
        recordingsService,
      });

      const repositoryMethods = {
        getAll: getAll<Recording>({
          store,
          service: {
            getAll: () => recordingsService.getAll(),
          },
          dictionary: recordingsDictionary,
        }),

        getAllByCommunity: getAllWithParams<Recording, RecordingParams>({
          store,
          service: recordingsService,
          dictionary: recordingsByCommunityDictionary,
        }),

        getOne: getOne<Recording>({
          store,
          service: recordingsService,
          dictionary: recordingsDictionary,
          onErrorCallback: () => {
            const currentUrl = router.url;
            const withoutLastSegment = currentUrl.split('/').slice(0, -1);
            router.navigate(withoutLastSegment);
          },
        }),

        addRecording: rxMethod<RecordingCreateRequest>(
          pipe(
            tap(() =>
              patchState(store, {
                isRecordingCreateLoading: true,
                isSelectedRecordingLoading: true,
                isRecordingsByCommunityLoading: true,
                isLoading: true,
              }),
            ),
            switchMap(dto =>
              recordingsService.add(dto).pipe(
                tap(newRecording => {
                  patchState(store, {
                    selectedRecording: newRecording,
                    recordings: sortAlphabetically([...store.recordings(), newRecording], sortRecordingsByCreatedAt),
                    recordingsByCommunity: dto.communityId
                      ? sortAlphabetically([...store.recordingsByCommunity(), newRecording], sortRecordingsByCreatedAt)
                      : [],
                    isRecordingsByCommunityLoading: false,
                    isSelectedRecordingLoading: false,
                    isRecordingCreateLoading: false,
                    isLoading: false,
                  });
                  if (dto.communityId) {
                    router.navigate(['marketing', 'communities', dto.communityId, 'recordings', newRecording.id, 'details']);
                  } else {
                    router.navigate(['marketing', 'recordings', newRecording.id, 'details']);
                  }
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedRecordingLoading: false,
                    isRecordingsByCommunityLoading: false,
                    isRecordingCreateLoading: false,
                    isLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateRecording: rxMethod<{ id: string; dto: RecordingUpdateRequest }>(
          pipe(
            tap(() =>
              patchState(store, {
                isSelectedRecordingLoading: true,
                isLoading: true,
                isRecordingsByCommunityLoading: true,
              }),
            ),
            switchMap(({ id, dto }) =>
              recordingsService.update(id, dto).pipe(
                tap(updatedRecording => {
                  patchState(store, {
                    selectedRecording: updatedRecording,
                    recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
                    recordingsByCommunity: findAndReplaceByIdWithSort(store.recordingsByCommunity(), updatedRecording),
                    isSelectedRecordingLoading: false,
                    isLoading: false,
                    isRecordingsByCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedRecordingLoading: false,
                    isLoading: false,
                    isRecordingsByCommunityLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        activateFileAndUpdateRecording: rxMethod<{ file: { id: string }; entity: { id: string; dto: RecordingUpdateRequest } }>(
          pipe(
            tap(() => patchState(store, { isSelectedRecordingLoading: true, isLoading: true, isRecordingsByCommunityLoading: true })),
            switchMap(({ file, entity }) =>
              fileStorageService.activateFile(file.id).pipe(
                tap(() => {
                  repositoryMethods.updateRecording({ id: entity.id, dto: { ...entity.dto } });
                }),
                catchError(() => {
                  notificationsService.showNotification('Failed to upload file, please try again.');
                  patchState(store, { isSelectedRecordingLoading: false, isLoading: false, isRecordingsByCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteFile: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ id }) =>
              fileStorageService.deleteFile(id).pipe(
                tap(() => {
                  patchState(store, {
                    isLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteRecording: deleteEntity<Recording>({
          store,
          service: recordingsService,
          dictionary: recordingsDictionary,
          onSuccessCallback: () => {
            patchState(store, { selectedRecording: null });
            router.navigate(['marketing', 'recordings']);
          },
        }),

        activate: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedRecordingLoading: true })),
            switchMap(({ id }) =>
              recordingsService.activate(id).pipe(
                tap(updatedRecording => {
                  patchState(store, {
                    recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
                    recordingsByCommunity: findAndReplaceByIdWithSort(store.recordingsByCommunity(), updatedRecording),
                    selectedRecording: updatedRecording,
                    isSelectedRecordingLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedRecordingLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deactivate: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedRecordingLoading: true })),
            switchMap(({ id }) =>
              recordingsService.deactivate(id).pipe(
                tap(updatedRecording => {
                  patchState(store, {
                    recordings: findAndReplaceByIdWithSort(store.recordings(), updatedRecording),
                    recordingsByCommunity: findAndReplaceByIdWithSort(store.recordingsByCommunity(), updatedRecording),
                    selectedRecording: updatedRecording,
                    isSelectedRecordingLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedRecordingLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        goToRecordingActions: async (id: string, communityId?: string): Promise<boolean> => {
          return communityId
            ? await router.navigate(['marketing', 'communities', communityId, 'recordings', id])
            : await router.navigate(['marketing', 'recordings', id]);
        },
        goToSelectedRecordingCommunityActions: async (recordingId: string, communityId: string): Promise<boolean> => {
          const result = await router.navigate(['marketing', 'recordings', recordingId, 'communities', communityId]);
          if (!result) return false;

          patchState(store, { selectedRecordingCommunityId: communityId });
          return true;
        },
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
        ...recordingCommunitiesMethods,
        ...recordingConfigurationMethods,
        ...recordingTopicsOrderStoreType,
      };
    },
  ),
);

export interface RecordingsStoreType
  extends RecordingCommunityHandlingStoreType, RecordingConfigurationStoreType, RecordingTopicsOrderStoreType {
  readonly recordings: Signal<Recording[]>;
  readonly isLoading: Signal<boolean>;
  readonly isRecordingCreateLoading: Signal<boolean>;
  readonly selectedRecording: Signal<Recording | null>;
  readonly isSelectedRecordingLoading: Signal<boolean>;
  readonly isFieldExpertsLoading: Signal<boolean>;
  readonly selectedRecordingCommunityId: Signal<string | null>;

  readonly recordingsByCommunity: Signal<Recording[]>;
  readonly isRecordingsByCommunityLoading: Signal<boolean>;

  readonly selectedRecordingId: Signal<string | null>;
  readonly selectedRecordingCommunity: Signal<RecordingsCommunity | null>;

  readonly topicsOrder: Signal<TopicOrder[]>;
  readonly isTopicsOrderLoading: Signal<boolean>;
  readonly isSetTopicsOrderLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly getAllByCommunity: (params: RecordingParams) => void;
  readonly getOne: (id: string) => void;
  readonly addRecording: (dto: RecordingCreateRequest) => void;
  readonly updateRecording: (input: { id: string; dto: RecordingUpdateRequest }) => void;
  readonly activateFileAndUpdateRecording: (input: { file: { id: string }; entity: { id: string; dto: RecordingUpdateRequest } }) => void;
  readonly deleteFile: (input: { id: string }) => void;
  readonly deleteRecording: (id: string) => void;
  readonly activate: (input: { id: string }) => void;
  readonly deactivate: (input: { id: string }) => void;

  readonly goToRecordingActions: (id: string, communityId?: string) => Promise<boolean>;
  readonly goToSelectedRecordingCommunityActions: (recordingId: string, communityId: string) => Promise<boolean>;
}

export function findAndReplaceByIdWithSort(list: Recording[], item: Recording) {
  return sortAlphabetically(
    findAndReplace<Recording>(list, item, i => i.id === item.id),
    sortRecordingsByCreatedAt,
  );
}

export function hasOnlySelectedCommunity(recording: Recording | null, community: Community | null): boolean {
  return recording?.communities.length === 1 && recording?.communities[0].id === community?.id;
}
